/**
 * V17.245: Local-timezone calendar date formatting.
 *
 * `date.toISOString()` converts to UTC before formatting. In Laos (UTC+7)
 * that means between 00:00 and 07:00 local time the ISO date is YESTERDAY,
 * and dates built at local midnight (e.g. `new Date(y, m, 1)`) always shift
 * back one day. Use this helper whenever a calendar date in the user's
 * timezone is intended: filter defaults, form defaults, date input min/max,
 * "today" comparisons.
 *
 * Lao local time root fix (brief 2026-10-09-001): the helpers below are
 * PINNED to Asia/Vientiane, so they give Lao dates and times whatever the
 * browser's own zone is. The backend stores naive UTC and now sends every
 * datetime with a `Z`; `parseApiDateTime` still reads an old naive string as
 * UTC, so `naive`, `…Z` and `…+00:00` are the same instant here.
 */

export const LAO_TZ = 'Asia/Vientiane';

// Laos has no DST — the offset is fixed.
const LAO_OFFSET = '+07:00';

// YYYY-MM-DDTHH:MM[:SS[.fff…]] with NO zone designator.
const NAIVE_DATETIME = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d+)?)?$/;

/** Parse an API datetime. A naive value is UTC (that is how the backend
 *  stores it); `Z` and `±HH:MM` are honoured; a plain date is that day. */
export function parseApiDateTime(value: string): Date {
  const s = value.trim();
  if (NAIVE_DATETIME.test(s)) {
    // JS Date keeps milliseconds only; trim microseconds so parsing is exact.
    return new Date(`${s.replace(/(\.\d{3})\d+$/, '$1')}Z`);
  }
  return new Date(s);
}

const laoParts = new Intl.DateTimeFormat('en-GB', {
  timeZone: LAO_TZ,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
});

function laoFields(d: Date): Record<'year' | 'month' | 'day' | 'hour' | 'minute', string> {
  const out = { year: '', month: '', day: '', hour: '', minute: '' };
  for (const p of laoParts.formatToParts(d)) {
    if (p.type in out) out[p.type as keyof typeof out] = p.value;
  }
  return out;
}

/** B2 (brief 2026-10-11-001): the Lao wall-clock parts of an instant, as numbers
 *  (month 1–12, weekday 0 = Sunday) — for the language-aware formatter in format.ts. */
export function laoDateParts(d: Date): {
  year: number; month: number; day: number; hour: number; minute: number; weekday: number;
} {
  const f = laoFields(d);
  const year = Number(f.year), month = Number(f.month), day = Number(f.day);
  return {
    year, month, day,
    hour: Number(f.hour), minute: Number(f.minute),
    weekday: new Date(Date.UTC(year, month - 1, day)).getUTCDay(),
  };
}

/** The Lao calendar date (YYYY-MM-DD) of an instant — default: now.
 *  Note: a Date built from browser-local parts (`new Date(y, m, d)`) is
 *  local midnight; that is the same Lao day in any browser at or WEST of
 *  UTC+7 (Laos, UTC, Europe, the Americas). East of Laos it would be the Lao
 *  day before — the app's users and CI are not there. */
export function toLocalDateStr(d: Date = new Date()): string {
  // Intl throws on an Invalid Date; keep the old helper's junk-but-no-crash result.
  if (Number.isNaN(d.getTime())) return 'NaN-NaN-NaN';
  const f = laoFields(d);
  return `${f.year}-${f.month}-${f.day}`;
}

/** The Lao wall clock (YYYY-MM-DDTHH:mm) of an instant, for a
 *  `<input type="datetime-local">` default — default: now. */
export function laoDateTimeLocalStr(d: Date = new Date()): string {
  const f = laoFields(d);
  return `${f.year}-${f.month}-${f.day}T${f.hour}:${f.minute}`;
}

/** A value typed as Lao wall-clock time and stored with no zone (e.g. the
 *  commercial call card's `activity_date`). The response edge appends `Z`
 *  to every naive datetime, which would be wrong for these — so drop any
 *  designator and attach the Lao offset. */
export function laoWallClockToIso(value: string): string {
  const m = /^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2})(:\d{2})?/.exec(value.trim());
  if (!m) return value;
  return `${m[1]}${m[2] ?? ':00'}${LAO_OFFSET}`;
}

/** A Lao wall-clock value (e.g. from a datetime-local input) as a UTC ISO
 *  instant, whatever the browser's zone. */
export function laoWallClockToUtcIso(value: string): string {
  return new Date(laoWallClockToIso(value)).toISOString();
}
